import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import {
  deliveryDetailUrl,
  backOfficeDriverUrl,
  codMethodLabel,
  type CodLegType,
  type CodOrderDetail,
  type CodOrderLeg,
  type CodOrderRider,
  getCodOrderDetail,
  riderWhatsAppUrl,
  shipmentDetailUrl,
} from '@/services/api/codOrders';
import AddProofModal from './AddProofModal';
import CodProofThumb from './CodProofThumb';
import ConfirmCodModal from './ConfirmCodModal';
import CodTimeline from './CodTimeline';
import {
  CodStatusBadge,
  formatDateTime,
  formatDiscrepancy,
  formatRupiah,
  formatTimeInStatus,
} from './CodBadges';

/**
 * One label/value pair.
 *
 * `whitespace-pre-line` because free-text fields (notes, discrepancy notes) are
 * typed by operators who use line breaks to separate points — collapsing them
 * runs the whole thing into one paragraph and loses the structure they
 * deliberately put there.
 */
const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ label, children, wide }) => (
  <div className={`flex flex-col gap-0.5 ${wide ? 'col-span-2' : ''}`}>
    <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
      {label}
    </span>
    <span className="whitespace-pre-line text-sm text-text-strong-950">
      {children}
    </span>
  </div>
);

/**
 * A titled card.
 *
 * Cards rather than hairline rules between blocks: the four blocks here are
 * different KINDS of thing — the order, two custody handoffs, an audit trail —
 * and a shared border with some padding reads as one long list where the eye
 * has to work out where each section begins.
 */
const Section: React.FC<{
  title: string;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, badge, action, children }) => (
  <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0">
    <header className="flex items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium text-text-strong-950">{title}</h3>
        {badge}
      </div>
      {action}
    </header>
    <div className="px-4 py-4">{children}</div>
  </section>
);

/**
 * The rider, with both of the things an operator does with one.
 *
 * Name and code as ONE link rather than two lines: they identify the same
 * person, and splitting them made the code look like a separate field with no
 * action. The link is keyed by the numeric id — the code is what a human
 * recognises, the id is what the back-office route resolves — so a snapshot
 * carrying no id renders as plain text instead of a link that 404s.
 */
const RiderBlock: React.FC<{ rider: CodOrderRider | null }> = ({ rider }) => {
  if (!rider) {
    // Not a gap: nobody is on record as having collected this cash, which is
    // exactly what Finance needs to know before chasing anyone.
    return (
      <p className="text-sm text-text-sub-600">
        Belum ada rider — delivery ini tidak pernah lewat dispatch logistic dan
        tidak ada event yang membawa driver.
      </p>
    );
  }

  const profileUrl = backOfficeDriverUrl(rider.id);
  const waUrl = riderWhatsAppUrl(rider.phone);

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
      <Field label="Nama & kode">
        {profileUrl ? (
          <a
            href={profileUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-accent hover:text-accent-dark hover:underline"
          >
            {rider.name} ({rider.code})
          </a>
        ) : (
          <span className="font-medium">
            {rider.name} ({rider.code})
          </span>
        )}
      </Field>
      <Field label="Nomor telepon">
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:text-accent-dark hover:underline"
          >
            {rider.phone}
          </a>
        ) : (
          // No number is information too — there is no way to reach them.
          <span className="text-text-sub-600">Tidak tersedia</span>
        )}
      </Field>
    </div>
  );
};

/**
 * A leg's evidence, plus the button that adds more.
 *
 * `+ Tambah bukti` is add-only and stays available after Finance has confirmed
 * — which is exactly when it is needed, since a dispute attachment (a Berita
 * Acara) arrives after the money is settled and correction is locked by then.
 */
const ProofBlock: React.FC<{
  leg: CodOrderLeg;
  legLabel: string;
  onAdd: () => void;
}> = ({ leg, legLabel, onAdd }) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
        Bukti &amp; Lampiran
      </span>
      <Button style="stroke" tone="neutral" size="xs" onClick={onAdd}>
        + Tambah bukti
      </Button>
    </div>

    {leg.proofFiles.length === 0 ? (
      <span className="text-xs text-text-sub-600">
        Tanpa bukti — tidak dilampirkan saat konfirmasi
      </span>
    ) : (
      <div className="flex flex-wrap items-start gap-3">
        {leg.proofFiles.map((proof) => (
          <CodProofThumb key={proof.imageUrl} proof={proof} />
        ))}
      </div>
    )}

    <span className="text-[11px] text-text-sub-600">
      Bukti bersifat tambah-terus — bisa ditambah walau {legLabel} sudah
      selesai, misalnya saat dispute.
    </span>
  </div>
);

/**
 * One custody leg, with the figure it was measured against made explicit.
 *
 * The acuan is the order's ORIGINAL COD amount for BOTH legs (Joshua,
 * 2026-09-07): one reference for the whole screen, so a reader comparing the
 * two cards is comparing like with like.
 *
 * `selisih` is therefore derived here — `confirmedAmount − orderAmount` — and
 * not read from the stored `discrepancyAmount`. The stored figure is measured
 * against the server's own per-leg baseline (Leg 2 against what the rider
 * handed over), so on an order whose Leg 1 came up short the two differ, and
 * printing the stored one beside this acuan would show three numbers that do
 * not add up. What the server recorded is still visible in full: it is what
 * `Catatan Selisih` on the leg explains, and the timeline keeps the figures as
 * they were confirmed.
 */
const LegBlock: React.FC<{
  leg: CodOrderLeg | null;
  emptyLabel: string;
  legLabel: string;
  /** `detail.amount` — the order's original COD, the acuan for both legs. */
  orderAmount: number;
  /** Opens the add-proof form for THIS leg. */
  onAddProof: () => void;
}> = ({ leg, emptyLabel, legLabel, orderAmount, onAddProof }) => {
  if (!leg) {
    return <p className="text-sm text-text-sub-600">{emptyLabel}</p>;
  }

  const discrepancy = leg.confirmedAmount - orderAmount;
  const short = discrepancy !== 0;

  return (
    <div className="flex flex-col gap-4">
      {/* The three money figures together, because the whole point of a leg is
          whether they agree. Split across the grid below they would need
          re-assembling by eye. */}
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3 rounded-lg bg-bg-weak-50 px-4 py-3">
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
            Acuan
          </span>
          <span className="text-sm tabular-nums text-text-sub-600">
            {formatRupiah(orderAmount)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
            Jumlah
          </span>
          <span className="text-lg font-medium tabular-nums text-text-strong-950">
            {formatRupiah(leg.confirmedAmount)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
            Selisih
          </span>
          <span
            className={`text-sm tabular-nums ${
              short
                ? 'font-medium text-state-warning-dark'
                : 'text-text-sub-600'
            }`}
          >
            {formatDiscrepancy(discrepancy)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Metode">{codMethodLabel(leg.method)}</Field>
        <Field label="Dikonfirmasi oleh">{leg.confirmedBy}</Field>
        <Field label="Waktu">{formatDateTime(leg.confirmedAt)}</Field>
        {leg.rider && (
          <Field label="Rider saat itu">
            {leg.rider.name} ({leg.rider.code})
          </Field>
        )}
        {leg.discrepancyNote && (
          <Field label="Catatan selisih" wide>
            {leg.discrepancyNote}
          </Field>
        )}
        {leg.notes && (
          <Field label="Catatan" wide>
            {leg.notes}
          </Field>
        )}
      </div>

      {/* `items-start`, not centred: the proof is a 104px thumbnail and the
          corrected-by line is one row of 11px text — centring leaves it
          floating halfway down the image with nothing to align to. */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-t border-stroke-soft-200 pt-3">
        <ProofBlock leg={leg} legLabel={legLabel} onAdd={onAddProof} />
        {leg.updatedBy && (
          <span className="text-[11px] text-text-sub-600">
            Dikoreksi oleh {leg.updatedBy} · {formatDateTime(leg.updatedAt)}
          </span>
        )}
      </div>
    </div>
  );
};

type ModalMode =
  | { kind: 'confirm-leg1' }
  | { kind: 'confirm-leg2' }
  | { kind: 'correct'; legType: CodLegType }
  /** Add-only, and separate from `correct` on purpose: it stays available after
   *  the money is settled, when correction is locked. */
  | { kind: 'add-proof'; legType: CodLegType }
  | null;

const LegStatusBadge: React.FC<{ closed: boolean }> = ({ closed }) => (
  <Badge status={closed ? 'success' : 'faded'} size="sm">
    {closed ? 'Selesai' : 'Belum'}
  </Badge>
);

const CodOrderDetailPanel: React.FC<{
  shipmentId: string;
  onClose: () => void;
  /** Refresh the list behind the drawer after anything is written. */
  onChanged: () => void;
  /**
   * Jump straight to the confirm modal for whichever leg is due.
   *
   * Set when the drawer was opened by a row's Confirm button: making the
   * operator find the same button again inside the drawer would undo the point
   * of putting it on the row.
   */
  autoConfirm?: boolean;
}> = ({ shipmentId, onClose, onChanged, autoConfirm = false }) => {
  const [detail, setDetail] = useState<CodOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>(null);

  // `onClose` comes from the parent as an inline arrow, so it is a new function
  // on every render. Depending on it here would make `load` unstable, the
  // effect below re-run every render, and the detail refetch in a loop — so it
  // is held in a ref instead.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await getCodOrderDetail(shipmentId));
    } catch (error) {
      toast.error((error as Error).message);
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Open the due leg's modal ONCE, after the detail lands (which leg is due
   * depends on its status, so this cannot run before the fetch).
   *
   * The latch is the whole point. Without it, closing the modal sets `modal` to
   * null while `autoConfirm` is still true — so the effect immediately reopens
   * it and the operator can never get out. `modal` cannot be the guard for the
   * same reason it is the thing being guarded.
   */
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoConfirm || !detail || autoOpened.current) return;
    autoOpened.current = true;
    if (detail.status === 'outstanding') setModal({ kind: 'confirm-leg1' });
    else if (detail.status === 'pending_deposit')
      setModal({ kind: 'confirm-leg2' });
  }, [autoConfirm, detail]);

  const afterWrite = useCallback(() => {
    setModal(null);
    void load();
    onChanged();
  }, [load, onChanged]);

  // The server re-checks all of this; these only decide what to render.
  const canConfirmLeg1 = detail?.status === 'outstanding';
  const canConfirmLeg2 = detail?.status === 'pending_deposit';

  return (
    <>
      <FormModal
        open
        onClose={onClose}
        position="right"
        size="xl"
        title={detail?.bookingId ?? 'Detail COD'}
        subtitle={
          detail ? (
            // A span, not a div: FormModal renders `subtitle` inside Radix's
            // DialogDescription, which is a <p>. A <div> there is invalid HTML —
            // React logs "cannot be a descendant of <p>. This will cause a
            // hydration error" on every open, and the browser silently closes
            // the <p> early, which is how the layout drifts.
            <span className="flex flex-wrap items-center gap-2">
              <CodStatusBadge status={detail.status} />
              <span className="text-sm text-text-sub-600">
                {formatRupiah(detail.amount)} · {detail.paymentMethod ?? '—'}
              </span>
            </span>
          ) : undefined
        }
        bodyClassName="bg-bg-weak-50"
        footer={
          <div className="flex justify-end gap-2">
            {canConfirmLeg1 && (
              <Button onClick={() => setModal({ kind: 'confirm-leg1' })}>
                Confirm COD · Rider → PIC
              </Button>
            )}
            {canConfirmLeg2 && (
              <Button onClick={() => setModal({ kind: 'confirm-leg2' })}>
                Confirm Deposit · PIC → Finance
              </Button>
            )}
            <Button style="stroke" tone="neutral" onClick={onClose}>
              Tutup
            </Button>
          </div>
        }
      >
        {loading || !detail ? (
          <p className="text-sm text-text-sub-600">Memuat…</p>
        ) : (
          <div className="flex flex-col gap-4">
            <Section title="Pesanan">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Client">{detail.clientName ?? '—'}</Field>
                <Field label="Hub">{detail.hubName ?? '—'}</Field>
                <Field label="Waybill">
                  {/* Straight to the shipment's OWN detail panel, not a list
                      filtered to one row — the COD ledger is keyed by shipment
                      id, so the id that addresses the panel is already here.
                      New tab: this drawer holds a half-read reconciliation, and
                      following a waybill should not be the thing that closes
                      it. */}
                  {shipmentDetailUrl(detail.id) ? (
                    <a
                      href={shipmentDetailUrl(detail.id) as string}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:text-accent-dark hover:underline"
                    >
                      {detail.waybill}
                    </a>
                  ) : (
                    detail.waybill
                  )}
                </Field>
                <Field label="Delivery ID">
                  {/* Back-office is a different app, so a new tab keeps this
                      drawer open behind it. */}
                  {deliveryDetailUrl(detail.deliveryUid) ? (
                    <a
                      href={deliveryDetailUrl(detail.deliveryUid) as string}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:text-accent-dark hover:underline"
                    >
                      {detail.deliveryUid}
                    </a>
                  ) : (
                    (detail.deliveryUid ?? '—')
                  )}
                </Field>
                <Field label="Owner (PIC)">{detail.owner ?? '—'}</Field>
                <Field label="Waktu di tahap ini">
                  {formatTimeInStatus(detail.stageStartedAt, detail.status)}
                </Field>
                <Field label="Dibuat">{formatDateTime(detail.createdAt)}</Field>
                {/* When the delivery completed — the date the board sorts and
                    filters by. "—" while still in delivery, or returned. */}
                <Field label="Tanggal selesai">
                  {formatDateTime(detail.completedAt)}
                </Field>
              </div>
            </Section>

            {/* Its own card, not a field in the order grid: the rider is who
                Finance chases, and both of its actions live here — open the
                rider in back-office, or message them. Buried in an eight-field
                grid those actions read as body text. */}
            <Section title="Rider">
              <RiderBlock rider={detail.rider} />
            </Section>

            <Section
              title="Leg 1 · Rider → PIC"
              badge={<LegStatusBadge closed={!!detail.legRiderToPic} />}
              action={
                detail.legRiderToPic ? (
                  <Button
                    style="stroke"
                    tone="neutral"
                    size="xs"
                    onClick={() =>
                      setModal({ kind: 'correct', legType: 'rider_to_pic' })
                    }
                  >
                    Koreksi
                  </Button>
                ) : undefined
              }
            >
              <LegBlock
                leg={detail.legRiderToPic}
                emptyLabel="Rider belum menyerahkan uang ke PIC."
                legLabel="Leg 1 · Rider → PIC"
                onAddProof={() =>
                  setModal({ kind: 'add-proof', legType: 'rider_to_pic' })
                }
                orderAmount={detail.amount}
              />
            </Section>

            <Section
              title="Leg 2 · PIC → Finance"
              badge={<LegStatusBadge closed={!!detail.legPicToFinance} />}
              action={
                detail.legPicToFinance ? (
                  <Button
                    style="stroke"
                    tone="neutral"
                    size="xs"
                    onClick={() =>
                      setModal({ kind: 'correct', legType: 'pic_to_finance' })
                    }
                  >
                    Koreksi
                  </Button>
                ) : undefined
              }
            >
              <LegBlock
                leg={detail.legPicToFinance}
                emptyLabel="PIC belum menyetor uang ke Finance."
                legLabel="Leg 2 · PIC → Finance"
                onAddProof={() =>
                  setModal({ kind: 'add-proof', legType: 'pic_to_finance' })
                }
                orderAmount={detail.amount}
              />
            </Section>

            <Section title="Riwayat">
              <CodTimeline entries={detail.timeline} detail={detail} />
            </Section>
          </div>
        )}
      </FormModal>

      {modal?.kind === 'add-proof' && detail && (
        <AddProofModal
          key={`${detail.id}-add-proof-${modal.legType}`}
          shipmentId={detail.id}
          legType={modal.legType}
          onClose={() => setModal(null)}
          onDone={afterWrite}
        />
      )}

      {modal && modal.kind !== 'add-proof' && detail && (
        <ConfirmCodModal
          // Same reason as the panel's key, one level down: switching between
          // Leg 1, Leg 2 and a correction within one order must re-seed the
          // amount and method from THAT leg's baseline, not keep the last
          // form's values.
          key={`${detail.id}-${modal.kind}-${
            modal.kind === 'correct' ? modal.legType : ''
          }`}
          mode={modal}
          detail={detail}
          onClose={() => setModal(null)}
          onDone={afterWrite}
        />
      )}
    </>
  );
};

export default CodOrderDetailPanel;
